/* Copyright (c) 2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable, Injector, Optional, Inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { Logger, LoggingService } from 'crimson/lib/logging/logging.service';
import { TimeoutNotificationConfig } from '../components/nv-generic-dialog/nv-generic-dialog.types';
import { WebWorkerService, WEB_WORKER_ADAPTER } from 'crimson/lib/utils/web-worker-adapter.interface';

// Default timeout notification constants (used when not specified in config)
const DefaultHalfwayWarningRatio = 0.5; // Halfway point (50% elapsed = 50% remaining)
const DefaultLastCountdownRemainingMs = 60000; // Last countdown shows exactly 1 minute remaining (fixed)
const MillisecondsPerMinute = 60000;

// Snackbar configuration constants
const SnackbarDurationMs = 5000;
const SnackbarHorizontalPosition = 'left' as const;
const SnackbarVerticalPosition = 'bottom' as const;

export enum TimeoutWarningType {
    Initial = 'initial',
    Halfway = 'halfway',
    LastCountdown = 'lastCountdown',
    Expired = 'expired'
}

export enum TimeoutWarningUrgency {
    Polite = 'polite',
    Assertive = 'assertive'
}

export enum TimeoutWarningIndex {
    Halfway = 0,
    LastCountdown = 1,
    Expired = 2
}

export interface TimeoutWarningEvent {
    type: TimeoutWarningType;
    minutes: number;
    messageKey: string;
    urgency: TimeoutWarningUrgency;
}

@Injectable()
export class SnackbarTimeoutNotificationService {
    private timerSubscription?: Subscription;
    private timerSubject = new Subject<{ timerId: string; startTimer: boolean; timeout: number }>();
    private activeTimers = new Map<string, boolean>();
    private setTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
    private timeoutWarningSubject = new Subject<TimeoutWarningEvent>();
    private destroy$ = new Subject<boolean>();
    private timeoutConfig: TimeoutNotificationConfig | null = null;
    private instanceId: string = '';
    private startTime: number = 0;
    private currentWarningIndex: number = 0;
    private logger?: Logger;

    /**
     * Observable that emits timeout warning events
     */
    get timeoutWarning$(): Observable<TimeoutWarningEvent> {
        return this.timeoutWarningSubject.asObservable();
    }

    constructor(
        private injector: Injector,
        @Optional() @Inject(WEB_WORKER_ADAPTER) private webWorkerService?: WebWorkerService,
        @Optional() private loggingService?: LoggingService
    ) {
        if (this.loggingService) {
            this.logger = this.loggingService.getLogger('SnackbarTimeoutNotificationService');
        }

        this.setupTimerSubscription();
    }

    /**
     * Start tracking timeout notifications
     * Uses a single timer that reschedules itself for each warning
     */
    start(config: TimeoutNotificationConfig): void {
        if (!this.validateConfig(config)) {
            return;
        }

        // Stop any existing timeout before starting a new one
        this.stop();
        this.instanceId = `timeout-${Date.now()}`;
        this.timeoutConfig = config;
        this.startTime = Date.now();
        this.currentWarningIndex = 0;

        // Show initial warning immediately
        const warningElapsedTimes = this.calculateTimeoutDurations(config.totalDuration, config);
        const remainingMinutes = this.calculateRemainingMinutesForTimeout(config.totalDuration, warningElapsedTimes);
        this.emitWarning(
            TimeoutWarningType.Initial,
            remainingMinutes.initial,
            config.warningMessageKey,
            TimeoutWarningUrgency.Polite
        );

        // Start tracking with single timer (works with both timer service and setTimeout)
        if (this.webWorkerService && this.timerSubscription) {
            this.startTimeoutTrackingWithTimerService(config);
        } else {
            this.startTimeoutTrackingWithSetTimeout(config);
        }
    }

    /**
     * Stop all timeout tracking and clear timers
     */
    stop(): void {
        this.clearTimeoutTracking();
        this.timeoutConfig = null;
        this.instanceId = '';
        // Dismiss any open snackbars when stopped
        this.dismissSnackbar();
    }

    /**
     * Cleanup resources (called when service is destroyed)
     */
    destroy(): void {
        this.stop();
        this.destroy$.next(true);
        this.destroy$.complete();
        this.timerSubscription?.unsubscribe();
        this.timerSubject.complete();
        this.timeoutWarningSubject.complete();
        this.activeTimers.clear();
        // Dismiss any open snackbars
        this.dismissSnackbar();
    }

    /**
     * Validates TimeoutNotificationConfig object
     * @param config Configuration object to validate
     * @returns true if config is valid, false otherwise
     */
    private validateConfig(config: TimeoutNotificationConfig): boolean {
        if (!config) {
            this.logger?.error('TimeoutNotificationConfig is required');
            return false;
        }

        if (!config.totalDuration || config.totalDuration <= 0) {
            this.logger?.error('TimeoutNotificationConfig.totalDuration must be a positive number');
            return false;
        }

        if (!config.warningMessageKey || config.warningMessageKey.trim() === '') {
            this.logger?.error('TimeoutNotificationConfig.warningMessageKey is required');
            return false;
        }

        if (!config.expiredMessageKey || config.expiredMessageKey.trim() === '') {
            this.logger?.error('TimeoutNotificationConfig.expiredMessageKey is required');
            return false;
        }

        return true;
    }

    private setupTimerSubscription(): void {
        if (this.webWorkerService) {
            this.timerSubscription = this.timerSubject
                .pipe(
                    switchMap(timerRequest => {
                        this.activeTimers.set(timerRequest.timerId, timerRequest.startTimer);
                        return this.webWorkerService!.intervalHandler(
                            { startTimer: timerRequest.startTimer, timeout: timerRequest.timeout },
                            (response: string) => this.onTimerUpdate(timerRequest.timerId, response)
                        );
                    }),
                    takeUntil(this.destroy$)
                )
                .subscribe();
        }
    }

    private startTimeoutTrackingWithTimerService(config: TimeoutNotificationConfig): void {
        this.scheduleNextWarningWithTimerService(config);
    }

    private scheduleNextWarningWithTimerService(config: TimeoutNotificationConfig): void {
        const warningElapsedTimes = this.calculateTimeoutDurations(config.totalDuration, config);
        const warningTimes = [
            warningElapsedTimes.halfwayElapsed,
            warningElapsedTimes.lastCountdownElapsed,
            config.totalDuration
        ];

        if (this.currentWarningIndex >= warningTimes.length) {
            return; // All warnings have been shown
        }

        const nextWarningTime = warningTimes[this.currentWarningIndex];
        const elapsed = Date.now() - this.startTime;
        const remaining = Math.max(0, nextWarningTime - elapsed);

        const timerId = `${this.instanceId}-warning-${this.currentWarningIndex}`;
        this.activeTimers.set(timerId, true);
        this.timerSubject.next({
            timerId,
            startTimer: true,
            timeout: remaining
        });
    }

    private startTimeoutTrackingWithSetTimeout(config: TimeoutNotificationConfig): void {
        this.scheduleNextWarningWithSetTimeout(config);
    }

    private scheduleNextWarningWithSetTimeout(config: TimeoutNotificationConfig): void {
        const warningElapsedTimes = this.calculateTimeoutDurations(config.totalDuration, config);
        const warningTimes = [
            warningElapsedTimes.halfwayElapsed,
            warningElapsedTimes.lastCountdownElapsed,
            config.totalDuration
        ];

        if (this.currentWarningIndex >= warningTimes.length) {
            return; // All warnings have been shown
        }

        const nextWarningTime = warningTimes[this.currentWarningIndex];
        const elapsed = Date.now() - this.startTime;
        const remaining = Math.max(0, nextWarningTime - elapsed);

        this.setTimeoutTimer = setTimeout(() => {
            // Check if service was stopped/destroyed before timer fired
            if (!this.timeoutConfig || this.instanceId === '') {
                return;
            }
            this.handleWarningFired(config, warningElapsedTimes);
            this.currentWarningIndex++;
            this.scheduleNextWarningWithSetTimeout(config);
        }, remaining);
    }

    private handleWarningFired(
        config: TimeoutNotificationConfig,
        warningElapsedTimes: {
            halfwayElapsed: number;
            lastCountdownElapsed: number;
        }
    ): void {
        // Only process if config still exists (component not destroyed)
        if (!config || !this.timeoutConfig) {
            return;
        }

        const remainingMinutes = this.calculateRemainingMinutesForTimeout(config.totalDuration, warningElapsedTimes);

        switch (this.currentWarningIndex) {
            case TimeoutWarningIndex.Halfway:
                this.emitWarning(
                    TimeoutWarningType.Halfway,
                    remainingMinutes.halfway,
                    config.warningMessageKey,
                    TimeoutWarningUrgency.Polite
                );
                break;
            case TimeoutWarningIndex.LastCountdown:
                this.emitWarning(
                    TimeoutWarningType.LastCountdown,
                    remainingMinutes.last,
                    config.warningMessageKey,
                    TimeoutWarningUrgency.Assertive
                );
                break;
            case TimeoutWarningIndex.Expired:
                this.emitWarning(
                    TimeoutWarningType.Expired,
                    0,
                    config.expiredMessageKey,
                    TimeoutWarningUrgency.Assertive
                );
                break;
        }
    }

    private onTimerUpdate(timerId: string, response: string): void {
        if (!this.activeTimers.get(timerId)) {
            return; // Timer was cancelled
        }

        if (response === 'Timeout' || response === 'Cleared') {
            this.activeTimers.delete(timerId);

            if (response === 'Timeout' && this.timeoutConfig && this.instanceId !== '') {
                const warningElapsedTimes = this.calculateTimeoutDurations(
                    this.timeoutConfig.totalDuration,
                    this.timeoutConfig
                );
                this.handleWarningFired(this.timeoutConfig, warningElapsedTimes);
                this.currentWarningIndex++;
                this.scheduleNextWarningWithTimerService(this.timeoutConfig);
            }
        }
    }

    private emitWarning(
        type: TimeoutWarningEvent['type'],
        minutes: number,
        messageKey: string,
        urgency: TimeoutWarningUrgency
    ): void {
        // Show snackbar if services are available
        this.showSnackbar(messageKey, minutes);

        // Emit event for component to handle (e.g., closing dialog on 'expired')
        this.timeoutWarningSubject.next({
            type,
            minutes,
            messageKey,
            urgency
        });
    }

    private showSnackbar(messageKey: string, minutes: number): void {
        try {
            const snackBar = this.getSnackBar();
            const translateService = this.getTranslateService();
            if (snackBar && translateService) {
                const message = translateService.instant(messageKey, { expiryTimeoutDurationInMinutes: minutes });
                snackBar.open(message, '', {
                    duration: SnackbarDurationMs,
                    horizontalPosition: SnackbarHorizontalPosition,
                    verticalPosition: SnackbarVerticalPosition,
                    panelClass: ['nv-hig', 'timeout-snackbar']
                });
            }
        } catch (e) {
            // Services not available, continue without showing snackbar
            this.logger?.error('Failed to show snackbar', e);
        }
    }

    private dismissSnackbar(): void {
        try {
            const snackBar = this.getSnackBar();
            snackBar?.dismiss();
        } catch (e) {
            // Service not available, ignore
            this.logger?.error('Failed to dismiss snackbar', e);
        }
    }

    private getSnackBar(): MatSnackBar | null {
        try {
            return this.injector.get(MatSnackBar, null);
        } catch (e) {
            this.logger?.error('Failed to get MatSnackBar', e);
            return null;
        }
    }

    private getTranslateService(): TranslateService | null {
        try {
            return this.injector.get(TranslateService, null);
        } catch (e) {
            this.logger?.error('Failed to get TranslateService', e);
            return null;
        }
    }

    private clearTimeoutTracking(): void {
        // Cancel active web worker timer for this instance
        if (this.timerSubscription && this.instanceId) {
            this.activeTimers.forEach((_, timerId) => {
                if (timerId.startsWith(this.instanceId)) {
                    this.timerSubject.next({
                        timerId,
                        startTimer: false,
                        timeout: 0
                    });
                    this.activeTimers.delete(timerId);
                }
            });
        }

        // Clear setTimeout timer
        if (this.setTimeoutTimer) {
            clearTimeout(this.setTimeoutTimer);
            this.setTimeoutTimer = null;
        }

        // Reset state
        this.currentWarningIndex = 0;
        this.startTime = 0;
    }

    private calculateTimeoutDurations(
        totalDuration: number,
        config: TimeoutNotificationConfig
    ): {
        halfwayElapsed: number;
        lastCountdownElapsed: number;
    } {
        const intervals = config.alertTimeoutIntervals || {};
        const halfwayRatio = intervals.halfwayWarningRatio ?? DefaultHalfwayWarningRatio;
        const lastCountdownRemaining = intervals.lastCountdownRemainingMs ?? DefaultLastCountdownRemainingMs;

        const halfwayDuration = Math.floor(totalDuration * halfwayRatio);
        // Last countdown is based on remaining time before expiration
        const lastCountdownElapsed = Math.max(0, totalDuration - lastCountdownRemaining);

        return {
            halfwayElapsed: halfwayDuration,
            lastCountdownElapsed
        };
    }

    private calculateRemainingMinutesForTimeout(
        totalDuration: number,
        warningElapsedTimes: { halfwayElapsed: number; lastCountdownElapsed: number }
    ): { initial: number; halfway: number; last: number } {
        // Calculate remaining minutes (not elapsed) for warning messages
        const halfwayRemaining = totalDuration - warningElapsedTimes.halfwayElapsed;
        const lastCountdownRemaining = totalDuration - warningElapsedTimes.lastCountdownElapsed;

        return {
            initial: Math.floor(totalDuration / MillisecondsPerMinute),
            halfway: Math.floor(halfwayRemaining / MillisecondsPerMinute),
            last: Math.floor(lastCountdownRemaining / MillisecondsPerMinute)
        };
    }
}
